import React from "react";
import {
  PickersDay as PickersDayBase,
  pickersDayClasses,
} from "@mui/x-date-pickers";
import { Tooltip, Box } from "@mui/material";
import CheckSharpIcon from "@mui/icons-material/CheckSharp";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

type CreativeUpdateEligibilityDaysProps = {
  day: moment.MomentInput;
  outsideCurrentMonth: boolean;
  canUpdateDate: [moment.MomentInput, moment.MomentInput];
  canNotUpdateDate: moment.MomentInput[];
  startDate?: moment.MomentInput;
  selected?: boolean;
  [key: string]: unknown;
};

const PickersDay = PickersDayBase as React.ComponentType<Record<string, any>>;

function CreativeUpdateEligibilityDays({
  day,
  outsideCurrentMonth,
  canUpdateDate,
  canNotUpdateDate,
  startDate,
  ...other
}: CreativeUpdateEligibilityDaysProps) {
  const isCanNotUpdateDay =
    canNotUpdateDate.length > 0
      ? moment(day).isBetween(
          canNotUpdateDate[0],
          canNotUpdateDate[1],
          "day",
          "[]"
        )
      : false;

  const isCanUpdateDay = moment(day).isBetween(
    canUpdateDate[0],
    canUpdateDate[1],
    "day",
    "[]"
  );

  const isStartDate = startDate
    ? moment(day).isSame(moment(startDate), "day")
    : false;

  const tooltipContent = isStartDate ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <strong>Start Date</strong>
    </Box>
  ) : isCanNotUpdateDay ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <strong>Creative Update:</strong> Not Eligible
      <CloseIcon sx={{ color: "error.main", fontSize: "12px" }} />
    </Box>
  ) : isCanUpdateDay ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <strong>Creative Update:</strong> Eligible
      <CheckSharpIcon
        sx={{ color: "var(--status-success)", fontSize: "14px" }}
      />
    </Box>
  ) : null;

  const dayComponent = (
    <PickersDay
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      {...other}
      autoFocus={false}
      sx={(theme) => ({
        position: "relative",
        ...(isStartDate && {
          border: `1px solid ${theme.palette.primary.main}`,
          borderRadius: "var(--radius-round)",
          color: "var(--neutral-700)",
          [`&.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.primary.dark + "!important",
            color: theme.palette.primary.contrastText,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: "var(--radius-round)",
          },
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: "var(--radius-round)",
          },
        }),
        ...(isCanNotUpdateDay && {
          border: "1px solid var(--status-warning)",
          color: "var(--neutral-700)",
          [`&.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.primary.dark + "!important",
            color: theme.palette.primary.contrastText,
            border: "1px solid var(--status-warning)",
          },
          "&:hover": {
            backgroundColor: "var(--status-warning-tint)",
            color: "var(--status-warning)",
            border: "1px solid var(--status-warning)",
            borderRadius: "var(--radius-round)",
          },
        }),
        ...(isCanUpdateDay && {
          border: `1px solid ${theme.palette.success.main}`,
          color: "var(--neutral-700)",
          [`&.${pickersDayClasses.selected}`]: {
            backgroundColor: theme.palette.primary.dark + "!important",
            color: theme.palette.primary.contrastText,
            border: `1px solid ${theme.palette.success.main}`,
          },
          "&:hover": {
            backgroundColor: theme.palette.success.main,
            border: `1px solid ${theme.palette.success.main}`,
            borderRadius: "var(--radius-round)",
          },
        }),
        ...(other.selected && {
          backgroundColor: theme.palette.primary.dark + "!important",
          color: theme.palette.primary.contrastText,
        }),
      })}
    />
  );

  const wrappedDayComponent = isStartDate ? (
    <Box>{dayComponent}</Box>
  ) : (
    dayComponent
  );

  // Always show tooltip if it's the start date, even if there's no other tooltip content
  if (!tooltipContent && !isStartDate) {
    return dayComponent;
  }

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      placement="top"
      slotProps={{
        popper: {
          disablePortal: false,
          style: { zIndex: 99999 },
        },
        tooltip: {
          sx: {
            backgroundColor: "var(--neutral-white)",
            color: "var(--palette-text-primary)",
            fontSize: "9px",
            padding: "6px 12px",
            boxShadow: "var(--shadow-floating)",
            "& .MuiTooltip-arrow": {
              color: "var(--neutral-white)",
            },
          },
        },
      }}
    >
      {wrappedDayComponent}
    </Tooltip>
  );
}

export default CreativeUpdateEligibilityDays;
